/**
 * TranslateButton — iter-232c (Feb 2026)
 *
 * Small icon button that calls `POST /api/utils/translate` and pipes the
 * result into the sibling field. Used across the Cloud GM AI Control Hub forms
 * next to every bilingual field (name / description / capabilities).
 *
 * Props
 *   sourceText  — value of the source-language field
 *   source      — ISO code of the source ('ar' | 'en')
 *   target      — ISO code of the target ('ar' | 'en')
 *   onTranslated(text) — receiver that writes into the target field
 *   testid      — data-testid for the button
 *   compact     — true → icon-only mini button (36×36); false → labelled pill
 */
import { useState } from 'react';
import axios from 'axios';
import { Languages, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function TranslateButton({
  sourceText,
  source,
  target,
  onTranslated,
  testid,
  compact = true,
  isAr = true,
}) {
  const [busy, setBusy] = useState(false);
  const t = (a, e) => (isAr ? a : e);

  const run = async () => {
    const text = (sourceText || '').trim();
    if (!text) {
      toast.error(
        source === 'ar'
          ? t('املأ الحقل العربي أولاً', 'Fill the Arabic field first')
          : t('املأ الحقل الإنجليزي أولاً', 'Fill the English field first'),
      );
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/utils/translate`,
        { text, source, target },
        { headers: getAuthHeaders() },
      );
      const out = r.data?.translated;
      if (!out) throw new Error('empty');
      onTranslated?.(out);
      toast.success(t('✓ تمت الترجمة', '✓ Translated'));
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          t('فشل الترجمة — تحقّق من مفتاح Gemini', 'Translation failed — check Gemini key'),
      );
    } finally {
      setBusy(false);
    }
  };

  const label = target === 'en' ? 'AR → EN' : 'EN → AR';
  const cls =
    'inline-flex items-center gap-1 rounded-lg border border-[#d4af37]/40 bg-[#d4af37]/10 text-[#d4af37] hover:bg-[#d4af37]/20 transition disabled:opacity-50';

  if (compact) {
    return (
      <button
        type="button"
        onClick={run}
        disabled={busy}
        title={t(`ترجمة ${label}`, `Translate ${label}`)}
        className={`${cls} h-7 px-2 text-[10px] font-mono`}
        data-testid={testid}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
        <span dir="ltr">{label}</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      className={`${cls} h-8 px-3 text-xs`}
      data-testid={testid}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />}
      {t('ترجمة', 'Translate')} <span dir="ltr" className="font-mono">{label}</span>
    </button>
  );
}
